import React from 'react';
import styled from 'styled-components';

import { TooltipSnapSettings } from '../../../../viewer/tools/utils/snap-settings-status';
import { Coordinates } from '../../../../viewer/utilities/math-helpers';
import { useFloatingPosition } from '../../../hooks/use-floating-position';
import { AreaProperties, AreaProps } from './components/area-properties';
import { AsbuiltGeometryProps, AsbuiltProperties } from './components/asbuilt-properties';
import { CoordinateProps } from './components/coordinate-properties';
import { DesignHoverProperties, DesignHoverProps } from './components/design-hover-properties';
import {
  DesignMultiselectProperties,
  DesignMultiselectProps,
} from './components/design-multiselect-properties';
import { HeightLayers, TooltipHeight } from './components/height-layers';
import { LineProperties, LineProps } from './components/line-properties';
import { SnapSettingsInfo } from './components/snap-settings-info';
import {
  VolumeInspectorProperties,
  VolumeInspectorProps,
} from './components/volume-inspector-properties';

function hasTooltipContent(
  props: Pick<SkyviewAssetPositionProps, 'properties' | 'heights'>,
): boolean {
  const { properties } = props;

  if ((props.heights?.length ?? 0) > 0) {
    return true;
  }

  switch (properties.type) {
    case 'coordinates':
      return false;
    case 'line':
      return true;
    case 'design':
      return true;
    default:
      return true;
  }
}

export interface SkyviewAssetPositionProps {
  properties:
    | CoordinateProps
    | AreaProps
    | LineProps
    | DesignHoverProps
    | DesignMultiselectProps
    | AsbuiltGeometryProps
    | VolumeInspectorProps;
  coordinateSystem?: string;
  coordinate: Coordinates;
  /** The surfaces straight above and below the point, when all heights are shown. */
  heights?: TooltipHeight[];
  /** The snap settings of what is snapped, see getSnapSettingsStatus. */
  snapSettings?: TooltipSnapSettings;
  screenPoint: {
    x: number;
    y: number;
  };
}

const SkyviewAssetPositionTooltip = (props: SkyviewAssetPositionProps) => {
  const { ref, floatingStyles, floatingProps } = useFloatingPosition({
    middleware: 'flip',
    offset: {
      mousePointer: 10,
      viewportEdge: 5,
    },
    followMousePointer: true,
    startPos: {
      x: props.screenPoint.x,
      y: props.screenPoint.y,
    },
    strategy: 'absolute',
  });

  if (!hasTooltipContent(props)) {
    return null;
  }

  return (
    <Component ref={ref} style={floatingStyles} {...floatingProps}>
      <AssetPositionProperties {...props} />
    </Component>
  );
};

/**
 * The content of the tooltip, without the box that follows the cursor.
 */
const AssetPositionProperties = (props: Omit<SkyviewAssetPositionProps, 'screenPoint'>) => {
  return (
    <>
      {props.properties.type === 'asbuilt-geometry' && (
        <AsbuiltProperties data={props.properties.data} />
      )}
      {props.properties.type === 'area' && (
        <AreaProperties
          coordinate={props.coordinate}
          coordinateSystem={props.coordinateSystem}
          properties={props.properties}
        />
      )}
      {props.properties.type === 'line' && (
        <LineProperties
          coordinate={props.coordinate}
          coordinateSystem={props.coordinateSystem}
          properties={props.properties}
        />
      )}
      {props.properties.type === 'design' && props.properties.isMultiselect && (
        <DesignMultiselectProperties
          coordinate={props.coordinate}
          coordinateSystem={props.coordinateSystem}
          properties={props.properties}
        />
      )}
      {props.properties.type === 'design' && !props.properties.isMultiselect && (
        <DesignHoverProperties
          coordinate={props.coordinate}
          coordinateSystem={props.coordinateSystem}
          properties={props.properties}
        />
      )}
      {props.properties.type === 'volume-inspector' && (
        <VolumeInspectorProperties
          coordinate={props.coordinate}
          coordinateSystem={props.coordinateSystem}
          properties={props.properties}
        />
      )}
      {props.snapSettings && <SnapSettingsInfo snapSettings={props.snapSettings} />}
      {props.heights && props.heights.length > 0 && <HeightLayers heights={props.heights} />}
    </>
  );
};

const Component = styled.div`
  position: absolute;
  z-index: 1000000000;

  width: min-content;

  padding: 0.5em;
  border-radius: 0.5em;

  background-color: rgba(64, 64, 64, 0.9);
  color: #fff;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);
  font-size: 10pt;

  font-family: 'Montserrat', sans-serif;

  pointer-events: none;
`;

export { AssetPositionProperties, hasTooltipContent, SkyviewAssetPositionTooltip };
